/**
 * As unidades da federação, e o município que pertence a cada uma.
 *
 * ## Por que a UF é lista e não texto livre
 *
 * `uf` era `char(2)` sem lista, e município era texto livre. Isso produz
 * "MG", "Mg", "mg" e "Minas" na mesma coluna, e "Ipatinga", "IPATINGA" e
 * "Ipatinga " na coluna ao lado — quatro grafias e uma cidade. Nenhum relatório
 * por região fecha depois disso, e o conserto retroativo é adivinhação.
 *
 * As 27 são fato e não mudam: a última alteração no mapa federativo foi em
 * 1988. Por isso elas ficam aqui, no código, e não num cadastro que alguém
 * precisaria manter.
 *
 * Os **municípios** são outra história — o Brasil cria um de vez em quando —, e
 * por isso eles vêm da lista do IBGE, gerada por `scripts/gerar-municipios.mjs`
 * e carregada sob demanda.
 */

export const UFS = [
  { sigla: 'AC', nome: 'Acre' },
  { sigla: 'AL', nome: 'Alagoas' },
  { sigla: 'AP', nome: 'Amapá' },
  { sigla: 'AM', nome: 'Amazonas' },
  { sigla: 'BA', nome: 'Bahia' },
  { sigla: 'CE', nome: 'Ceará' },
  { sigla: 'DF', nome: 'Distrito Federal' },
  { sigla: 'ES', nome: 'Espírito Santo' },
  { sigla: 'GO', nome: 'Goiás' },
  { sigla: 'MA', nome: 'Maranhão' },
  { sigla: 'MT', nome: 'Mato Grosso' },
  { sigla: 'MS', nome: 'Mato Grosso do Sul' },
  { sigla: 'MG', nome: 'Minas Gerais' },
  { sigla: 'PA', nome: 'Pará' },
  { sigla: 'PB', nome: 'Paraíba' },
  { sigla: 'PR', nome: 'Paraná' },
  { sigla: 'PE', nome: 'Pernambuco' },
  { sigla: 'PI', nome: 'Piauí' },
  { sigla: 'RJ', nome: 'Rio de Janeiro' },
  { sigla: 'RN', nome: 'Rio Grande do Norte' },
  { sigla: 'RS', nome: 'Rio Grande do Sul' },
  { sigla: 'RO', nome: 'Rondônia' },
  { sigla: 'RR', nome: 'Roraima' },
  { sigla: 'SC', nome: 'Santa Catarina' },
  { sigla: 'SP', nome: 'São Paulo' },
  { sigla: 'SE', nome: 'Sergipe' },
  { sigla: 'TO', nome: 'Tocantins' },
] as const;

export type Uf = (typeof UFS)[number]['sigla'];

const SIGLAS: ReadonlySet<string> = new Set(UFS.map((u) => u.sigla));

export function ehUf(valor: unknown): valor is Uf {
  return typeof valor === 'string' && SIGLAS.has(valor);
}

/** O nome por extenso, para a tela não mostrar só duas letras. */
export function nomeDaUf(sigla: string): string | null {
  return UFS.find((u) => u.sigla === sigla)?.nome ?? null;
}

/**
 * Os municípios de uma UF, carregados sob demanda.
 *
 * São ~80 KB para o país inteiro. Com `import()` dinâmico, quem nunca abre um
 * formulário de endereço não paga por eles — e o pacote principal da tela já
 * passa de 1 MB, então acrescentar a lista nele seria cobrar de todo mundo por
 * uma tela que poucos abrem.
 */
export async function municipiosDa(uf: Uf): Promise<readonly string[]> {
  const { MUNICIPIOS_POR_UF } = await import('./municipios.gerado');
  return MUNICIPIOS_POR_UF[uf] ?? [];
}

/**
 * O CEP como oito dígitos, ou `null`.
 *
 * Aceita o que a pessoa digita de verdade — "35160-000", "35160 000",
 * "35.160-000" — e guarda só os dígitos. Formatar na gravação e não na
 * digitação é o que faz colar um CEP de outro sistema funcionar.
 */
export function digitosDoCep(valor: string): string | null {
  const so = valor.replace(/\D/g, '');
  return so.length === 8 ? so : null;
}

/** "35160000" vira "35160-000". Vazio continua vazio. */
export function formatarCep(valor: string): string {
  const so = valor.replace(/\D/g, '').slice(0, 8);
  return so.length > 5 ? `${so.slice(0, 5)}-${so.slice(5)}` : so;
}

/**
 * O nome da lista do IBGE que corresponde ao gravado, ou o gravado como veio.
 *
 * O banco grava o municipio em caixa alta (0049: "IPATINGA") e a lista tem a
 * grafia do IBGE ("Ipatinga"). Sem esta volta, a edicao abria o endereco com o
 * municipio em branco — o valor gravado nao era nenhuma das opcoes.
 */
export function municipioDaLista(lista: readonly string[], gravado: string): string {
  const procurado = gravado.trim().toLocaleUpperCase('pt-BR');
  if (procurado === '') return gravado;
  return lista.find((nome) => nome.toLocaleUpperCase('pt-BR') === procurado) ?? gravado;
}
